// src/App.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import axios from 'axios';
import { useMempoolWebSocket } from './utils/useMempoolWebSocket';
import { useTimechainWebSocket } from './utils/useTimechainWebSocket';
import { useCoinCapWebSocket } from './utils/useCoinCapWebSocket';
import { setupNotifications, notificationUtils } from './utils/notifications';
import { priceAlertManager } from './utils/priceAlerts';
import { GlossaryProvider } from './contexts/GlossaryContext';
import { MasterWrap, AppHeader, AppFooter, AppContent } from './components/layout';
import { Preloader } from './Preloader';
import ScrollToTopButton from './components/decorations/ScrollToTopButton';
import ScrollToTop from './components/ScrollToTop';
import { PWAUpdateNotification } from './components/PWAUpdateNotification';
import { useVisibilityChange } from './utils/useVisibilityChange';
import { usePageLifecycle } from './utils/usePageLifecycle';
import { createBackgroundMonitor } from './utils/backgroundReconnect';
import { useRecoilState } from 'recoil';
import { formatNumber } from './utils';
import { memoizedParse, parseBlockData, updateRecentBlocksOptimized, scheduleCacheClear } from './utils/memoryOptimization';

import { recentBlocksState } from './recoilState';
// import Arcade from './components/games/Arcade';
import CustomDashboard from './components/custom/CustomDashboard';

// Use an environment variable to toggle between data sources
const USE_TIMECHAIN = process.env.REACT_APP_USE_TIMECHAIN === 'false';

// Simple flag to enable/disable the notification feature (0 = disabled, 1 = enabled)
const NOTIFICATION_FEATURE_ENABLED = 0;

function App() {
  const [apiStatus, setApiStatus] = useState(null);
  const [data, setData] = useState(null);
  const [bitcoinPrice, setBitcoinPrice] = useState(null);
  const fetchDataRef = useRef(null);

  // Removed glossaryData state - now using GlossaryContext
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Background monitor for WebSocket health
  const backgroundMonitorRef = useRef(null);

  // Initialize notifications on mount
  useEffect(() => {
    if (NOTIFICATION_FEATURE_ENABLED) {
      const initNotifications = async () => {
        const { permission } = await setupNotifications();
        // Check localStorage for user preference
        const storedPreference = localStorage.getItem('blockNotificationsEnabled');
        // Only enable if permission is granted AND user has explicitly enabled
        if (permission === 'granted' && storedPreference === 'true') {
          setNotificationsEnabled(true);
        } else {
          setNotificationsEnabled(false);
        }
      };
      initNotifications();
    } else {
      // Clean up notification-related localStorage items when feature is disabled
      localStorage.removeItem('blockNotificationsEnabled');
      localStorage.removeItem('priceAlerts');
      // Ensure notifications are disabled
      setNotificationsEnabled(false);
    }
  }, []);

  // Schedule periodic cache clearing to prevent memory buildup
  useEffect(() => {
    const interval = setInterval(() => {
      scheduleCacheClear();
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);


  // Start price alert monitoring when notifications are enabled
  useEffect(() => {
    if (NOTIFICATION_FEATURE_ENABLED && notificationsEnabled && bitcoinPrice) {
      priceAlertManager.startMonitoring(() => bitcoinPrice, 5000);
    } else {
      priceAlertManager.stopMonitoring();
    }

    return () => {
      priceAlertManager.stopMonitoring();
    };
  }, [notificationsEnabled, bitcoinPrice]);

  // Scroll to top on component mount (page refresh)
  useEffect(() => {
    // Disable browser's scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Force scroll to top immediately
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also handle window load event
    const handleLoad = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    window.addEventListener('load', handleLoad);
    
    // Also try with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
    
    // Clean up on unmount
    return () => {
      window.removeEventListener('load', handleLoad);
      clearTimeout(timeoutId);
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Initialize both websocket hooks
  const { bitcoinPrice: timechainBitcoinPrice, isTimechainActive } = useTimechainWebSocket();
  const { message: coincapMessage } = useCoinCapWebSocket();

  const { message: mempoolMessage, reconnect: reconnectMempoolWebSocket } = useMempoolWebSocket();
  const [lastMempoolBlockData, setLastMempoolBlockData] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [da, setDa] = useState(null);
  const [fees, setFees] = useState(null);
  const [mempoolBlocks, setMempoolBlocks] = useState(null);
  const [blocks, setBlocks] = useState(null);
  const [mempoolInfo, setMempoolInfo] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [vBytesPerSecond, setVBytesPerSecond] = useState(null);

  const [recentBlocks, setRecentBlocks] = useRecoilState(recentBlocksState);

  const [animateLastBlock, setAnimateLastBlock] = useState(false);
  const prevBlockHeightRef = useRef(null);

  // Set up background monitoring for WebSocket health
  useEffect(() => {
    const checkWebSocketHealth = () => {
      // This will trigger reconnection if needed
      reconnectMempoolWebSocket();
    };

    backgroundMonitorRef.current = createBackgroundMonitor(checkWebSocketHealth);
    backgroundMonitorRef.current.start();

    return () => {
      if (backgroundMonitorRef.current) {
        backgroundMonitorRef.current.stop();
      }
    };
  }, [reconnectMempoolWebSocket]);

  // Listen for service worker messages
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'CHECK_WEBSOCKETS') {
        // Service worker is asking us to check our WebSocket connections
        reconnectMempoolWebSocket();
      }
    };

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [reconnectMempoolWebSocket]);

  // Handle page lifecycle events for better background behavior
  usePageLifecycle({
    onFreeze: () => {
      // Browser is suspending the page
      // console.log('Page being frozen');
    },
    onResume: () => {
      // Page resuming from suspended state
      // console.log('Page resuming - reconnecting services');
      reconnectMempoolWebSocket();
      if (fetchDataRef.current) {
        fetchDataRef.current();
      }
    },
    onHidden: () => {
      // Page is now in background
      // console.log('Page hidden - maintaining connections');
    },
    onVisible: () => {
      // Page is now visible
      // console.log('Page visible - ensuring fresh data');
      if (fetchDataRef.current) {
        fetchDataRef.current();
      }
    }
  });

  useEffect(() => {
    if (lastMempoolBlockData && (!prevBlockHeightRef.current || prevBlockHeightRef.current < lastMempoolBlockData.height)) {
      setAnimateLastBlock(true);

      setTimeout(() => {
        setAnimateLastBlock(false);
      }, 20000); // Reset after animation duration

      // Trigger notification for new block
      if (notificationsEnabled && prevBlockHeightRef.current !== null && bitcoinPrice) {
        notificationUtils.showNewBlockNotification(lastMempoolBlockData, bitcoinPrice);
      }

      prevBlockHeightRef.current = lastMempoolBlockData.height;
    }
  }, [lastMempoolBlockData, notificationsEnabled, bitcoinPrice]);

  useEffect(() => {
    // Check if we have s07 price data available
    if (data?.s07?.price?.RAW?.BTC?.USD?.PRICE && !bitcoinPrice) {
      // Use s07 price as initial/fallback price
      const s07Price = data.s07.price.RAW.BTC.USD.PRICE;
      setBitcoinPrice(s07Price.toFixed(2));
    }
  }, [data, bitcoinPrice]);

  useEffect(() => {
    if (USE_TIMECHAIN) {
      // WebSocket price takes priority over s07
      if (timechainBitcoinPrice) {
        setBitcoinPrice(timechainBitcoinPrice);
      }
    } else {
      if (coincapMessage) {
        const myObj = memoizedParse(coincapMessage);
        const num = parseFloat(myObj['bitcoin']);
        if (!isNaN(num)) {
          const btcTicker = num.toFixed(2);
          setBitcoinPrice(btcTicker);
        }
      }
    }
  }, [timechainBitcoinPrice, coincapMessage]);

  useEffect(() => {
    const hasLatestBlock = (blockHeight) => recentBlocks.find((item) => item.height === blockHeight)
    const updateCondition = lastMempoolBlockData && lastMempoolBlockData.height && !hasLatestBlock(lastMempoolBlockData.height)

    if (updateCondition) {
      const optimizedBlocks = updateRecentBlocksOptimized(recentBlocks, lastMempoolBlockData);
      if (optimizedBlocks !== recentBlocks) {
        setRecentBlocks(optimizedBlocks);
      }
    }
  }, [lastMempoolBlockData, recentBlocks, setRecentBlocks])

  useEffect(() => {
    if (mempoolMessage) {
      const myObj = parseBlockData(mempoolMessage);
      // console.log("Mempool.Space data:", myObj);
      const {
        conversions,
        da,
        fees,
        mempoolInfo,
        transactions,
        vBytesPerSecond,
        block,
        blocks,
      } = myObj

      if (conversions) setConversions(conversions);
      if (da) setDa(da);
      if (fees) setFees(fees);
      if (myObj['mempool-blocks']) setMempoolBlocks(myObj['mempool-blocks']);

      if (blocks) {
        const latestBlock = blocks[blocks.length - 1];

        setBlocks(blocks);
        setRecentBlocks(blocks);
        setLastMempoolBlockData(latestBlock);

        // console.log("Blocks state:", blocks);
      }
      if (mempoolInfo) setMempoolInfo(mempoolInfo);
      if (transactions) setTransactions(transactions);
      if (vBytesPerSecond) setVBytesPerSecond(vBytesPerSecond);

      if (block) {
        setLastMempoolBlockData(block);
      }
    }
  }, [mempoolMessage, setRecentBlocks]);

  useVisibilityChange(() => {
    if (document.visibilityState === "visible") {
      reconnectMempoolWebSocket();
      
      // Immediately fetch fresh data when app becomes visible
      if (fetchDataRef.current) {
        fetchDataRef.current();
      }
      
      // Clear notification badge when app becomes visible
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {
          // Badge clearing not supported or failed
        });
      }
      
      // Also clear all notifications
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.ready.then(registration => {
          registration.getNotifications().then(notifications => {
            notifications.forEach(notification => notification.close());
          });
        });
      }
    }
  });

  useEffect(() => {
    if (bitcoinPrice) {
      const formattedPrice = parseFloat(bitcoinPrice).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      document.title = `${formattedPrice} Live Bitcoin Price`;
    } else {
      document.title = 'TimechainStats.com';
    }
  }, [bitcoinPrice]);

  const fetchDataFromAPI = useCallback((urls) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responsePromises = urls.map((url) => axios.get(url));
        const responses = await Promise.all(responsePromises);

        const combinedData = responses.reduce((acc, response) => {
          return { ...acc, ...response.data };
        }, {});

        resolve(combinedData);
      } catch (error) {
        // console.error("Error fetching data from API:", error);
        reject();
      }
    });
  }, []);

  useEffect(() => {
    // These keys are in the front-end so you can use them, too. Have fun!
    const fetchData = async () => {
      const urls = [
        "https://2140data.net/api/ts04/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts05/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts06/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts07/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts08/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts09/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts10/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts11/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05", // maybe 12
        "https://2140data.net/api/ts13/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts14/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts15/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/ts16/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s01/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s02/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s03/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        // "https://2140data.net/api/s04/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s06/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s07/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s08/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        // "https://2140data.net/api/s09/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05", // Temporarily disabled due to API error
        "https://2140data.net/api/s11/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s12/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s15/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",
        "https://2140data.net/api/s091/latest?api_key=EUL9E54951LU3L5FA65M2D9KZQUH7LL05",


      ];

      try {
        const combinedData = await fetchDataFromAPI(urls);
        setData(combinedData);
        setApiStatus(true);
        // console.log('Data fetched successfully:', combinedData);
      } catch {
        setApiStatus(false);
        // console.error('Failed to fetch data');
      }
    };
    
    // Store fetchData in ref for visibility change handler
    fetchDataRef.current = fetchData;

    const startIntervalAtNextFiveMinuteMark = () => {
      const now = new Date();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      const milliseconds = now.getMilliseconds();

      const timeToNextFiveMinuteMark = ((5 - ((minutes - 1) % 5)) * 60 * 1000) - (seconds * 1000) - milliseconds;

      fetchData();

      setTimeout(() => {
        fetchData();
        setInterval(fetchData, 5 * 60 * 1000);
      }, timeToNextFiveMinuteMark);
    };

    startIntervalAtNextFiveMinuteMark();

  }, [fetchDataFromAPI]);

  useEffect(() => {
    return () => {
      // console.log('App component unmounted');
    };
  }, []);

  // Glossary data is now loaded on-demand via GlossaryContext

  useEffect(() => {
    // console.log('Data updated:', data);
  }, [data]);

  // Removed glossary data logging

  return (
    <GlossaryProvider>
      <Router>
        <>
          <ScrollToTop />
        <Preloader
          data={data}
          bitcoinPrice={bitcoinPrice}
          lastMempoolBlockData={lastMempoolBlockData}
          fees={fees}
          mempoolInfo={mempoolInfo}
          da={da}
          formatNumber={formatNumber}
        />
        <MasterWrap>
          <AppHeader
            setApiStatus={setApiStatus}
            bitcoinPrice={bitcoinPrice}
            data={data}
            lastMempoolBlockData={lastMempoolBlockData}
            formatNumber={formatNumber}
            notificationsEnabled={notificationsEnabled}
            setNotificationsEnabled={setNotificationsEnabled}
            notificationFeatureEnabled={NOTIFICATION_FEATURE_ENABLED}
          />
          <Routes>
            <Route path="/" element={
              <AppContent
                    data={data}
                bitcoinPrice={bitcoinPrice}
                lastMempoolBlockData={lastMempoolBlockData}
                conversions={conversions}
                da={da}
                fees={fees}
                mempoolBlocks={mempoolBlocks}
                blocks={blocks}
                mempoolInfo={mempoolInfo}
                transactions={transactions}
                vBytesPerSecond={vBytesPerSecond}
                isTimechainActive={isTimechainActive}
              />
            } />

            {/* <Route path="/arcade" element={
              <Arcade
                bitcoinPrice={bitcoinPrice}
                lastMempoolBlockData={lastMempoolBlockData}
                  />
            } /> */}

            <Route
              path="/donate"
              element={
                <AppContent
                        data={data}
                  bitcoinPrice={bitcoinPrice}
                  lastMempoolBlockData={lastMempoolBlockData}
                  conversions={conversions}
                  da={da}
                  fees={fees}
                  mempoolBlocks={mempoolBlocks}
                  blocks={blocks}
                  mempoolInfo={mempoolInfo}
                  transactions={transactions}
                  vBytesPerSecond={vBytesPerSecond}
                  isTimechainActive={isTimechainActive}
                />
              }
            />

            <Route
              path="/custom"
              element={
                <CustomDashboard
                  data={data}
                  bitcoinPrice={bitcoinPrice}
                  lastMempoolBlockData={lastMempoolBlockData}
                  conversions={conversions}
                  da={da}
                  fees={fees}
                  mempoolBlocks={mempoolBlocks}
                  blocks={blocks}
                  mempoolInfo={mempoolInfo}
                  transactions={transactions}
                  vBytesPerSecond={vBytesPerSecond}
                  isTimechainActive={isTimechainActive}
                  animate={animateLastBlock}
                />
              }
            />

          </Routes>
          <ScrollToTopButton />
          <PWAUpdateNotification />
          <AppFooter
            apiStatus={apiStatus}
            isTimechainActive={isTimechainActive}
          />
        </MasterWrap>
        </>
      </Router>
    </GlossaryProvider>
  );
}

export default App;